import { usePrincipal } from '@/common/principal/PrincipalContext';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { openSseStream } from './sseClient';

/**
 * Holds the browser's single push stream to gtw. Tabs elect a leader through a Web Lock; only the
 * leader opens the stream and relays every push to the other tabs over a BroadcastChannel, so a
 * browser uses one HTTP connection however many tabs are open.
 *
 * Push types: connected { streamUuid, resumed }, entity-change, notifs, module-up (see docs/SseMigrationPlan.md).
 */
const PushStreamContext = createContext({
  streamUuid: null,
  isLeaderRef: { current: false },
  addListener: () => () => {},
});

export const PushStreamProvider = ({ children }) => {
  const { principal, getToken, login } = usePrincipal();
  const username = principal.username;

  // Shared by all tabs, so a new leader reuses the stream the modules' subscriptions refer to
  const streamUuid = useMemo(() => {
    const key = `kr-push-stream-uuid:${username}`;
    let uuid = localStorage.getItem(key);
    if (!uuid) {
      uuid = crypto.randomUUID();
      localStorage.setItem(key, uuid);
    }
    return uuid;
  }, [username]);

  const isLeaderRef = useRef(false);

  // { [type]: Set<listener> }
  const listenersRef = useRef({});

  const dispatch = useCallback((type, data) => {
    listenersRef.current[type]?.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error(`[Push] ${type} listener failed`, error);
      }
    });
  }, []);

  /**
   * @returns {() => void} removes the listener
   */
  const addListener = useCallback((type, listener) => {
    const listeners = (listenersRef.current[type] ??= new Set());
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel(`kr-push:${username}`);
    channel.onmessage = ({ data: { type, data } }) => dispatch(type, data);

    const lockName = `kr-push-leader:${username}`;
    const lockAbort = new AbortController();
    let releaseLock;
    let closeStream;

    navigator.locks
      .query()
      .then(({ held, pending }) => {
        // Another tab holds or is queued for the stream: this tab will take over a live stream later
        const takeover = [...held, ...pending].some((lock) => lock.name === lockName);
        let connectedBefore = false;

        return navigator.locks.request(lockName, { signal: lockAbort.signal }, () => {
          isLeaderRef.current = true;
          console.log('[Push] this tab is the leader and holds the push stream');
          closeStream = openSseStream({
            url: `/gtw/api/push/stream?streamUuid=${streamUuid}`,
            getToken,
            onUnauthorized: login,
            onEvent: (type, data) => {
              if (type === 'connected') {
                // Resumed streams may have missed pushes, so listeners should refresh
                data = { ...data, resumed: takeover || connectedBefore };
                connectedBefore = true;
              }
              channel.postMessage({ type, data });
              dispatch(type, data);
            },
          });
          // Held until unmount or tab close; the browser then grants the lock to the next tab
          return new Promise((resolve) => {
            releaseLock = resolve;
          });
        });
      })
      .catch(() => {}); // Aborted on unmount while still waiting

    return () => {
      isLeaderRef.current = false;
      closeStream?.();
      releaseLock?.();
      lockAbort.abort();
      channel.close();
    };
  }, [username, streamUuid]);

  const value = useMemo(() => ({ streamUuid, isLeaderRef, addListener }), [streamUuid, addListener]);

  return <PushStreamContext.Provider value={value}>{children}</PushStreamContext.Provider>;
};

export const usePushStream = () => useContext(PushStreamContext);
